// ========== داده‌ها و کمک‌تابع‌های چاپ پیش‌فاکتور ==========

/**
 * نشانی صفحه‌ی «شرایط عمومی فروش». QR چاپ پیش‌فاکتور همین نشانی را نگه می‌دارد تا
 * اسکن آن مرورگر را مستقیم به این صفحه ببرد. اگر سرور در print-data مقدار
 * sales_terms.source_url را بفرستد، همان اولویت دارد و این فقط پشتیبان است.
 */
export const SALE_TERMS_URL = "https://mahanpanel.com/saleterms/";

/**
 * نشانی نهایی QR. سرور نشانی را بدون اسلش پایانی می‌فرستد، ولی مقدار خواسته‌شده در
 * مستند با اسلش است؛ اینجا یکدست می‌شود تا هر دو حالت به یک نتیجه برسند.
 */
export const saleTermsUrl = (sourceUrl?: string | null): string => {
  const raw = String(sourceUrl || "").trim() || SALE_TERMS_URL;
  return raw.endsWith("/") ? raw : `${raw}/`;
};

/**
 * محتوای QR هر پیش‌فاکتور. مختص همان فاکتور است تا با اسکن، فاکتور قابل شناسایی باشد.
 * (فعلاً استفاده نمی‌شود؛ QR چاپ به نشانی شرایط فروش تغییر کرده است.)
 */
export const invoiceQrValue = (invoice: {
  invoice_number?: number | string;
  invoice_date?: string;
  customer_name?: string;
  net_amount?: string | number | null;
}) =>
  [
    "MAHANPANEL",
    `INV:${invoice.invoice_number ?? "-"}`,
    `DATE:${invoice.invoice_date ?? "-"}`,
    `CUSTOMER:${invoice.customer_name ?? "-"}`,
    `AMOUNT:${invoice.net_amount ?? "-"}`,
  ].join("|");

// ========== تبدیل عدد به حروف فارسی ==========

const ONES = [
  "",
  "یک",
  "دو",
  "سه",
  "چهار",
  "پنج",
  "شش",
  "هفت",
  "هشت",
  "نه",
];

const TEENS = [
  "ده",
  "یازده",
  "دوازده",
  "سیزده",
  "چهارده",
  "پانزده",
  "شانزده",
  "هفده",
  "هجده",
  "نوزده",
];

const TENS = [
  "",
  "",
  "بیست",
  "سی",
  "چهل",
  "پنجاه",
  "شصت",
  "هفتاد",
  "هشتاد",
  "نود",
];

const HUNDREDS = [
  "",
  "صد",
  "دویست",
  "سیصد",
  "چهارصد",
  "پانصد",
  "ششصد",
  "هفتصد",
  "هشتصد",
  "نهصد",
];

// نام هر سه‌رقمی از راست به چپ.
const SCALES = ["", "هزار", "میلیون", "میلیارد", "بیلیون"];

/** یک عدد سه‌رقمی (۱ تا ۹۹۹) را به حروف تبدیل می‌کند. */
const threeDigitsToWords = (value: number): string => {
  const parts: string[] = [];
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;

  if (hundreds > 0) parts.push(HUNDREDS[hundreds]);

  if (rest >= 10 && rest < 20) {
    parts.push(TEENS[rest - 10]);
  } else {
    const tens = Math.floor(rest / 10);
    const ones = rest % 10;
    if (tens > 0) parts.push(TENS[tens]);
    if (ones > 0) parts.push(ONES[ones]);
  }

  return parts.join(" و ");
};

/**
 * عدد را به حروف فارسی تبدیل می‌کند؛ برای سطر «مبلغ به حروف» در پیش‌فاکتور.
 *
 * ورودی می‌تواند رشته‌ی Decimal سرور باشد. بخش اعشاری نادیده گرفته می‌شود چون
 * مبالغ ریالی صحیح‌اند.
 */
export const numberToPersianWords = (input: string | number | null | undefined): string => {
  if (input === null || input === undefined) return "صفر";

  const cleaned = String(input).replace(/[,\s]/g, "").split(".")[0].replace(/^\+/, "");
  const negative = cleaned.startsWith("-");
  const digits = (negative ? cleaned.slice(1) : cleaned).replace(/^0+(?=\d)/, "");

  if (!/^\d+$/.test(digits)) return "-";
  if (digits === "0") return "صفر";

  // عدد را از راست به گروه‌های سه‌رقمی می‌شکنیم.
  const groups: number[] = [];
  for (let i = digits.length; i > 0; i -= 3) {
    groups.push(Number(digits.slice(Math.max(0, i - 3), i)));
  }

  const words: string[] = [];
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    const group = groups[i];
    if (group === 0) continue;
    const scale = SCALES[i] || "";
    words.push(scale ? `${threeDigitsToWords(group)} ${scale}` : threeDigitsToWords(group));
  }

  return (negative ? "منفی " : "") + words.join(" و ");
};

/** جداکننده‌ی هزارگان برای مبالغ چاپی. */
export const printAmount = (value: string | number | null | undefined): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === undefined || num === null || isNaN(num)) return "0";
  return Math.round(num).toLocaleString("en-US");
};

/** عدد اعشاری با تعداد رقم ثابت (برای متراژ). */
export const printDecimal = (
  value: string | number | null | undefined,
  digits = 2
): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === undefined || num === null || isNaN(num)) return "0";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

/** رواداری ضخامت ورق: مقدار اصلی سمت چپ، رواداری سمت راست (با قفل جهت). */
export const printSheetThickness = (value?: number | string | null): string => {
  if (value === undefined || value === null || value === "") return "-";
  return `⁦${(Number(value) / 100).toFixed(2)} ± 0.02⁩`;
};
